import { type RefObject, useCallback, useEffect, useState } from "react";

const isLandscapeOrientation = () =>
	window.matchMedia?.("(orientation: landscape)").matches ||
	window.innerWidth > window.innerHeight;

export function useLandscapeOrientation() {
	const [isLandscape, setIsLandscape] = useState(true);

	useEffect(() => {
		const handleOrientationChange = () =>
			setIsLandscape(isLandscapeOrientation());
		handleOrientationChange();
		const mediaQuery = window.matchMedia("(orientation: landscape)");
		mediaQuery.addEventListener("change", handleOrientationChange);
		window.addEventListener("resize", handleOrientationChange);
		return () => {
			mediaQuery.removeEventListener("change", handleOrientationChange);
			window.removeEventListener("resize", handleOrientationChange);
		};
	}, []);

	return isLandscape;
}

export function useFullscreen(containerRef: RefObject<HTMLElement | null>) {
	const [fullscreenFallback, setFullscreenFallback] = useState(false);

	const disableFullscreenFallback = useCallback(() => {
		setFullscreenFallback(false);
	}, []);

	const enableFullscreenFallback = useCallback(() => {
		setFullscreenFallback(true);
	}, []);

	const exitFullscreen = useCallback(async () => {
		if (document.fullscreenElement) {
			await document.exitFullscreen?.();
		}
		disableFullscreenFallback();
	}, [disableFullscreenFallback]);

	const enterFullscreen = useCallback(async () => {
		const target = containerRef.current ?? document.documentElement;
		if (document.fullscreenElement) {
			return;
		}
		try {
			await target.requestFullscreen?.();
			setFullscreenFallback(false);
		} catch {
			enableFullscreenFallback();
		}
	}, [containerRef, enableFullscreenFallback]);

	return {
		fullscreenFallback,
		enterFullscreen,
		exitFullscreen,
	};
}
